import { QUICK_LEARN_EVENT } from '@/components/QuickLearnTour';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lightning } from '@phosphor-icons/react';
import { RUN_RESUME_EVENT, RUN_SIGNAL_EVENT, SPEED_RUN_EVENT, inSpeedRun, runBacktestFailed } from '@/lib/speedRun';
import { advance, jumpTo, nextStep, rememberSignal, runView, type Here } from '@/lib/speedRunEngine.core';
import type { RunPage } from '@/lib/speedRunPath.core';
import { askOrb } from '@/components/orbLive';
import { track } from '@/lib/analytics';

/****************
 * THE SPEED RUN ON THE WEB (Guillaume 11-01, the A-to-Z path). The phone draws
 * the same steps (mobile-app components/tour/SpeedRunGuide.tsx); both read
 * lib/speedRunPath.core.ts through lib/speedRunEngine.core.ts.
 *
 * It never navigates and never presses anything: the control glows and the
 * person presses it ("the goal is to show user how to do it not to do it for
 * him"). No click-catcher over the page: the glow is a ring drawn around the
 * control, so the control itself takes the click.
 ****************/

/** The pages of the run, on the web's addresses. */
export function webHere(pathname: string, search: string): Here {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return 'dashboard';
  if (p === '/traders') return 'traders';
  if (/^\/(traders|profile)\/(?!group(\/|$))[^/]+$/.test(p)) return 'trader';
  if (p === '/configure') return 'strategies';
  if (p.startsWith('/learn')) return 'learn';
  if (p === '/copy-trade' && new URLSearchParams(search).get('tab') === 'backtest') return ['copies', 'backtest'];
  if (p === '/sessions' || p === '/copy-trade' || p.startsWith('/session/')) return 'copies';
  return null;
}

/** The menu's name of each page (common.json). */
const PAGE_LABEL: Record<RunPage, string> = {
  dashboard: 'common:nav.dashboard',
  traders: 'common:nav.traders',
  trader: 'common:nav.traders',
  strategies: 'common:nav.strategies',
  copies: 'common:nav.myCopies',
  learn: 'common:nav.learn',
  backtest: 'common:nav.myCopies',
  any: 'common:nav.dashboard',
};

function firstOnScreen(names: string[]): HTMLElement | null {
  for (const n of names) {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${n}"]`))) {
      if (el.offsetWidth > 4 && el.offsetHeight > 4) return el;
    }
  }
  return null;
}

export function SpeedRunGuide() {
  const { t } = useTranslation('learn');
  const { pathname, search } = useLocation();
  const [on, setOn] = useState(inSpeedRun());
  const [tick, setTick] = useState(0);
  // The answer to the step's Yes / No question (RunStep.ask), for this step only.
  const [answer, setAnswer] = useState<{ step: string; yes: boolean } | null>(null);
  const [, setShown] = useState<string>('');
  const [ring, setRing] = useState<DOMRect | null>(null);
  const here = webHere(pathname, search);

  useEffect(() => {
    const sync = () => setOn(inSpeedRun());
    const onSignal = (e: Event) => {
      const name = (e as CustomEvent<string>).detail;
      if (name) rememberSignal(name);
      setTick((n) => n + 1);
    };
    const onResume = () => setTick((n) => n + 1);
    window.addEventListener(SPEED_RUN_EVENT, sync);
    window.addEventListener(RUN_SIGNAL_EVENT, onSignal);
    window.addEventListener(RUN_RESUME_EVENT, onResume);
    return () => {
      window.removeEventListener(SPEED_RUN_EVENT, sync);
      window.removeEventListener(RUN_SIGNAL_EVENT, onSignal);
      window.removeEventListener(RUN_RESUME_EVENT, onResume);
    };
  }, []);

  // The person reached a page, or a signal came: move on while the step is done.
  useEffect(() => {
    if (!on) return;
    const before = runView(here)?.step.id;
    advance(here);
    const after = runView(here)?.step.id;
    // Redraw on the new step (11-02 audit: a signal moved the stored step but the card stayed).
    if (after !== before) {
      void Promise.resolve().then(() => setShown(after ?? 'done'));
      // Others that depend on the step (the Pro lock) redraw too.
      try { window.dispatchEvent(new Event(SPEED_RUN_EVENT)); } catch { /* native */ }
    }
    if (after !== before) track('onboarding_step_done', 'speed_run', undefined, { step: after ?? 'done' });
    // The last step done (CopyFi Pro opened): the regular quick tour of the platform (Guillaume's path, its end).
    if (before && !after) window.setTimeout(() => window.dispatchEvent(new Event(QUICK_LEARN_EVENT)), 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, pathname, search, tick]);

  const view = on ? runView(here) : null;
  const glowKey = view ? view.glow.join(',') : '';

  // The ring follows the control (scrolling, a page still loading), and the page is
  // brought to it once per step (Guillaume 11-02: "scroll up or down to it if
  // necessary so the user don't have to look for it").
  useEffect(() => {
    // A new step's glow starts from nothing: never the last step's ring left in place (11-02 audit, /m step 5 kept step 3's ring on the Traders tab).
    setRing(null);
    if (!view || view.glow.length === 0) return;
    const names = view.glow;
    let shown: Element | null = null;
    let scrolledAt = 0;
    const orb = (): HTMLElement | null => document.querySelector('[data-orb-float]');
    const id = window.setInterval(() => {
      const el = firstOnScreen(names);
      if (el) {
        const r = el.getBoundingClientRect();
        const off = r.top < 64 || r.bottom > window.innerHeight - 24;
        // Brought on screen once per control, smoothly; still off a second later
        // (a smooth scroll that did not run), at once (11-02 audit: Run backtest stayed below the fold).
        if (el !== shown) { shown = el; scrolledAt = Date.now(); if (off) el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        else if (off && Date.now() - scrolledAt > 1000) { scrolledAt = Date.now(); el.scrollIntoView({ block: 'center' }); }
      }
      const rect = el ? el.getBoundingClientRect() : null;
      setRing(rect);
      // The floating Orb steps aside while it would sit on the glowing control (11-02 audit: it covered Continue).
      const o = orb();
      if (o) {
        const b = o.getBoundingClientRect();
        const hit = !!rect && b.left < rect.right && b.right > rect.left && b.top < rect.bottom && b.bottom > rect.top;
        o.style.visibility = hit ? 'hidden' : '';
      }
    }, 250);
    return () => { window.clearInterval(id); const o = orb(); if (o) o.style.visibility = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glowKey, view?.index]);

  if (!view) return null;
  const { step } = view;
  const key = `run.${step.id}`;
  const answered = answer && answer.step === step.id ? answer : null;
  // Others that depend on the step redraw too (11-02 audit: at the last step the Switch to Pro still showed its lock).
  const goOn = () => {
    setAnswer(null); nextStep(); setTick((n) => n + 1);
    try { window.dispatchEvent(new Event(SPEED_RUN_EVENT)); } catch { /* native */ }
  };
  const yes = () => {
    track('onboarding_step_done', 'speed_run', undefined, { step: `${step.id}:yes` });
    // A step that only asks goes on; one that waits for an action points at its glowing control.
    if (!step.doneWhen) goOn(); else setAnswer({ step: step.id, yes: true });
  };
  const no = () => {
    track('onboarding_step_done', 'speed_run', undefined, { step: `${step.id}:no` });
    if (step.ask?.noGoesOn) goOn(); else setAnswer({ step: step.id, yes: false });
  };
  const btn = 'rounded-md px-3 py-1.5 text-xs';
  const primary = `${btn} bg-orange-500 font-medium text-white hover:bg-orange-400`;
  const ghost = `${btn} border border-border hover:bg-white/5`;

  // The card leaves the bottom when the glowing control is there (11-02 audit: it covered Create Model and Start Paper Session).
  const cardOnTop = !!ring && view.glow.length > 0 && ring.bottom > window.innerHeight - 300;

  // On the page's top layer, above its pop-ups and dialogs (11-02 audit: the achievements
  // pop-up hid the card and the glow on its own button).
  return createPortal(
    <>
      {ring && view.glow.length > 0 && (
        <>
          {/* Above the header and the pop-ups (z 60), so it is never behind them; it flashes. */}
          <div aria-hidden className="pointer-events-none fixed z-[110] rounded-lg ring-2 ring-orange-400 animate-pulse"
            style={{ top: ring.top - 6, left: ring.left - 6, width: ring.width + 12, height: ring.height + 12, boxShadow: '0 0 0 4px rgba(251,146,60,.25), 0 0 24px 6px rgba(251,146,60,.45)' }}
            data-testid="speed-run-glow" />
          <div aria-hidden className="pointer-events-none fixed z-[110] rounded-lg border-2 border-orange-300 animate-ping"
            style={{ top: ring.top - 6, left: ring.left - 6, width: ring.width + 12, height: ring.height + 12, animationDuration: '1.6s' }} />
        </>
      )}
      <div role="dialog" aria-label={t(`${key}.title`)} data-testid="speed-run-card"
        className={`fixed ${cardOnTop ? 'top-20' : 'bottom-6'} left-1/2 z-[100] w-[min(380px,calc(100vw-32px))] -translate-x-1/2 rounded-xl border border-orange-400/40 bg-card/95 p-4 shadow-2xl backdrop-blur`}>
        <div className="mb-1 flex items-center text-[11px] text-orange-300">
          <span className="flex items-center gap-1.5"><Lightning size={12} weight="fill" /> {t('run.counter', { n: view.index + 1, total: view.total })}</span>
        </div>
        <p className="text-sm font-semibold">{t(`${key}.title`)}</p>
        {step.id === 'backtest' && runBacktestFailed() && <p className="mt-1 text-sm text-red-300">{t('run.backtest.failed')}</p>}
        <p className="mt-1 text-sm text-muted-foreground">{t(`${key}.body`)}</p>
        {view.goTo && (
          <p className="mt-2 text-sm text-orange-300">{t('run.goTo', { page: t(PAGE_LABEL[view.goTo]) })}</p>
        )}
        {step.ask && !view.goTo && !answered && <p className="mt-2 text-sm font-medium">{t(`${key}.q`)}</p>}
        {answered && <p className="mt-2 text-sm text-orange-300">{answered.yes ? t('run.pressGlow') : t(`${key}.no`)}</p>}
        <div className="mt-3 flex items-center justify-end gap-2">
          {step.orb && (
            <button type="button" onClick={() => { track('assistant_asked', `speedrun:${step.id}`); askOrb(t(`${key}.ask`)); }} className={ghost}>
              {t('run.askOrb')}
            </button>
          )}
          {step.ask && !view.goTo && !answered && (
            <>
              <button type="button" onClick={no} className={ghost}>{t('run.no')}</button>
              <button type="button" onClick={yes} className={primary}>{t('run.yes')}</button>
            </>
          )}
          {answered && !answered.yes && step.ask?.back && (
            <button type="button" onClick={() => { setAnswer(null); jumpTo(step.ask!.back!); setTick((n) => n + 1); }} className={ghost}>{t('run.back')}</button>
          )}
          {answered && !answered.yes && !step.doneWhen && (
            <button type="button" onClick={goOn} className={primary}>{t('run.next')}</button>
          )}
          {view.next && !step.ask && (
            <button type="button" onClick={goOn} className={primary}>{t('run.next')}</button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
